import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { InviteMemberButton } from "@components/common/invite-member-button";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { ProjectRoleAutoComplete } from "@components/role/role-autocomplete/project-role-autocomplete";
import { BaseProjectProps } from "@custom-types/project-types";
import { Dialog } from "@flat-ui";
import { useInviteMembersToProject } from "@hooks/projects/use-invite-members-to-project";
import { Stack } from "@mui/material";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { delay } from "@utils/time-utils";
import { useState } from "react";

interface Props extends BaseProjectProps {
  /**
   * Determines the variant of the invite member button
   *  - "outlined" (default): Contained button with white background and blue border
   *  - "filled": Normal button with blue background
   */
  buttonVariant?: "outlined" | "filled";
}

/** Renders the invite member button and the functionality in project overview */
export function InviteMemberToProject({
  project,
  buttonVariant = "outlined",
}: Props): JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const {
    autocompleteOptions,
    selectedMembers,
    helperText,
    handleMemberSelect,
    onInputChange,
    isValidInput,
    selectedRole,
    setSelectedRole,
    isLoading,
    shouldSendInviteDisable,
    handleConfirm,
    reset,
  } = useInviteMembersToProject({
    project,
    location: "invite-member-to-project",
    initialRole: "RESOURCE_VIEWER",
    // Close the dialog before turning off the loading state so the closing
    // animation cannot trigger a duplicate invitation request.
    onInviteSuccess: async () => {
      setIsDialogOpen(false);
      await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
    },
  });

  /** Default setup when invite dialog open */
  function onOpenInviteDialog(): void {
    reset();
    setIsDialogOpen(true);
  }

  return (
    <>
      <InviteMemberButton
        buttonVariant={buttonVariant}
        onClick={onOpenInviteDialog}
      />

      <Dialog
        title="Invite members to project"
        confirmText="Send Invite"
        open={isDialogOpen}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        isConfirmDisabled={shouldSendInviteDisable}
        onCancel={() => setIsDialogOpen(false)}
        dataTestId="invite-members-to-project-dialog"
      >
        <Stack marginBottom={SPACE_ELEMENTS_OF_MODAL}>
          <ProjectRoleAutoComplete
            isRequired={true}
            selectedRole={selectedRole}
            onChange={(role) => setSelectedRole(role)}
          />
        </Stack>
        <MembersAutocomplete
          isRequired
          hasAutoFocus
          label="Members"
          helperText={helperText}
          onInputChange={onInputChange}
          options={autocompleteOptions}
          selectedOptions={selectedMembers}
          handleMemberSelect={handleMemberSelect}
          isValid={isValidInput}
        />
      </Dialog>
    </>
  );
}
